import { FunctionComponent, useMemo } from "react";
import { Handle, Position } from "reactflow";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import BooleanSvg from "../../../../../svgs/parameter-type-icons/boolean.svg";
import DateTimeOffsetSvg from "../../../../../svgs/parameter-type-icons/date-time-offset.svg";
import DateTimeSvg from "../../../../../svgs/parameter-type-icons/date-time.svg";
import DateSvg from "../../../../../svgs/parameter-type-icons/date.svg";
import DecimalSvg from "../../../../../svgs/parameter-type-icons/decimal.svg";
import DoubleSvg from "../../../../../svgs/parameter-type-icons/double.svg";
import FileContentSvg from "../../../../../svgs/parameter-type-icons/file-content.svg";
import GuidSvg from "../../../../../svgs/parameter-type-icons/guid.svg";
import ImageSvg from "../../../../../svgs/parameter-type-icons/image.svg";
import Int16Svg from "../../../../../svgs/parameter-type-icons/int16.svg";
import Int32Svg from "../../../../../svgs/parameter-type-icons/int32.svg";
import Int64Svg from "../../../../../svgs/parameter-type-icons/int64.svg";
import LocationSvg from "../../../../../svgs/parameter-type-icons/location.svg";
import StringSvg from "../../../../../svgs/parameter-type-icons/string.svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaTextBold from "../../../../ui-kit/typography/TextBold/TextBold";

export interface FkPkHandle {
  id: string;
  type: "source" | "target";
  columnName: string;
}
interface Props {
  data: any;
  sourcePosition: "top" | "left" | "right" | "bottom";
  targetPosition: "top" | "left" | "right" | "bottom";
}

const CustomNode: FunctionComponent<Props> = (props) => {
  const { label, columns, sourceHandles, targetHandles } = props.data;

  const getTypeIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case "unknown":
        return <StringSvg />;
      case "boolean":
        return <BooleanSvg />;
      case "bytea":
        return <DoubleSvg />;
      case "byte":
        return <DoubleSvg />;
      case "date":
        return <DateSvg />;
      case "datetime":
        return <DateTimeSvg />;
      case "location":
        return <LocationSvg />;
      case "filecontent":
        return <FileContentSvg />;
      case "datetimeoffset":
        return <DateTimeOffsetSvg />;
      case "decimal":
        return <DecimalSvg />;
      case "double":
        return <DoubleSvg />;
      case "float":
        return <DoubleSvg />;
      case "varbinary":
        return <DoubleSvg />;
      case "guid":
        return <GuidSvg />;
      case "int16":
        return <Int16Svg />;
      case "int32":
        return <Int32Svg />;
      case "int64":
        return <Int64Svg />;
      case "single":
        return <DoubleSvg />;
      case "string":
        return <StringSvg />;
      case "image":
        return <ImageSvg />;
      case "int":
        return <Int32Svg />;
      case "bigint":
        return <Int64Svg />;
      case "smallint":
        return <Int16Svg />;
      case "nvarchar":
        return <StringSvg />;
      case "uniqueidentifier":
        return <GuidSvg />;
      case "bit":
        return <BooleanSvg />;
      default:
        return <StringSvg />;
    }
  };

  const getTypeName = (sqlDataType: string, dataLength: number) => {
    let potantialType = KMainFunctions.getTypeNameBySqlDataType(sqlDataType);

    if (potantialType == "Unknown") {
      potantialType = KMainFunctions.getTypeNameBySqlDataType(`${sqlDataType}(${dataLength})`);
    }

    return potantialType;
  };

  // Create maps for quick lookup of handles by column name
  const sourceHandleMap = useMemo(() => {
    const map = new Map<string, FkPkHandle[]>();
    if (sourceHandles) {
      sourceHandles.forEach((handle: FkPkHandle) => {
        const existing = map.get(handle.columnName) || [];
        existing.push(handle);
        map.set(handle.columnName, existing);
      });
    }
    return map;
  }, [sourceHandles]);

  const targetHandleMap = useMemo(() => {
    const map = new Map<string, FkPkHandle[]>();
    if (targetHandles) {
      targetHandles.forEach((handle: FkPkHandle) => {
        const existing = map.get(handle.columnName) || [];
        existing.push(handle);
        map.set(handle.columnName, existing);
      });
    }
    return map;
  }, [targetHandles]);

  // Get column names set for quick lookup
  const columnNames = useMemo(() => {
    const names = new Set<string>();
    if (columns) {
      columns.forEach((col: any) => names.add(col.name));
    }
    return names;
  }, [columns]);

  // Get handles that don't match any column (need to be rendered separately)
  const unmatchedSourceHandles = useMemo(() => {
    if (!sourceHandles) return [];
    return sourceHandles.filter((handle: FkPkHandle) => !columnNames.has(handle.columnName));
  }, [sourceHandles, columnNames]);

  const unmatchedTargetHandles = useMemo(() => {
    if (!targetHandles) return [];
    return targetHandles.filter((handle: FkPkHandle) => !columnNames.has(handle.columnName));
  }, [targetHandles, columnNames]);

  // Render a column row with its handles
  const renderColumn = (column: any, idx: number) => {
    const columnSourceHandles = sourceHandleMap.get(column.name) || [];
    const columnTargetHandles = targetHandleMap.get(column.name) || [];
    const hasHandles = columnSourceHandles.length > 0 || columnTargetHandles.length > 0;

    return (
      <div
        key={column.name || idx}
        className={`join-node__table-list__item${hasHandles ? " join-node__table-list__item--has-handle" : ""}`}
        title={column.name}
      >
        {/* Target handles on the left side of the column */}
        {columnTargetHandles.map((handle, handleIdx) => (
          <Handle
            key={`target-${handle.id}-${handleIdx}`}
            type="target"
            position={Position.Left}
            id={handle.id}
            className="join-node__column-handle join-node__column-handle--target"
            style={{ top: "50%" }}
          />
        ))}

        {getTypeIcon(getTypeName(column.sqlDataType, column.maxLength))}
        <KuikaSmallText>{column.name}</KuikaSmallText>

        {/* Source handles on the right side of the column */}
        {columnSourceHandles.map((handle, handleIdx) => (
          <Handle
            key={`source-${handle.id}-${handleIdx}`}
            type="source"
            position={Position.Right}
            id={handle.id}
            className="join-node__column-handle join-node__column-handle--source"
            style={{ top: "50%" }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="join-node" style={{ backgroundColor: "var(--dark-0)" }}>
      {/* Render unmatched handles that don't correspond to visible columns */}
      {/* These are hidden but necessary for React Flow edge connections */}
      {unmatchedSourceHandles.map((handle: FkPkHandle, idx: number) => (
        <Handle
          key={`unmatched-source-${handle.id}-${idx}`}
          type="source"
          position={Position.Right}
          id={handle.id}
          className="join-node__column-handle join-node__column-handle--source"
          style={{ top: `${10 + idx * 5}%`, opacity: 0, pointerEvents: "none" }}
        />
      ))}
      {unmatchedTargetHandles.map((handle: FkPkHandle, idx: number) => (
        <Handle
          key={`unmatched-target-${handle.id}-${idx}`}
          type="target"
          position={Position.Left}
          id={handle.id}
          className="join-node__column-handle join-node__column-handle--target"
          style={{ top: `${10 + idx * 5}%`, opacity: 0, pointerEvents: "none" }}
        />
      ))}
      <KuikaTooltip content={label} position="top">
        <div className="join-node__header">
          <KuikaTextBold>{label}</KuikaTextBold>
        </div>
      </KuikaTooltip>
      <div className="join-node__table-list nowheel">
        {columns?.map((column: any, idx: number) => renderColumn(column, idx))}
      </div>
    </div>
  );
};

export default CustomNode;
